import { debounce } from '@mui/material';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import { translate } from 'components/translation/translation';
import { Lens } from 'icone';
import React, { useEffect, useRef, useState } from 'react';
import { ButtonToolbar, FormControl, InputGroup } from 'react-bootstrap';
import AG_GRID_LOCALE_IT from 'components/ag-grid/locale-it';
import { customCellStyle } from 'lib/helpers/agGridHelpers';
import { formatToDecimals } from 'lib/helpers/formatting';
import { PeriodicReportingType } from '../PeriodicReporting';
import {
	downloadInvoicesOrTimesheetsByProjectIdAndStatementId,
	getTimesheetsByProjectIdAndStatementId,
	pickAllTimesheetsByProjectIdAndStatementId,
	unpickAllTimesheetsByProjectIdAndStatementId,
} from 'lib/api_ts/periodicReportingApi';
import CustomPagination from 'components/atomic/organisms/ProjectReporting/CustomPagination';
import { useLocation } from 'react-router-dom-v5-compat';
import { months } from 'lib/helpers/calendar';
import CustomTooltip from 'components/ag-grid/CustomTooltip';
import DownloadExcelButton from '@components/form/DownloadExcelButton';

export type InternalCostType = {
	id?: number;
	beneficiaryName: string;
	serialNumber: string;
	category: string;
	number: string;
	day: number;
	month: number;
	year: number;
	orderNumber: string;
	wbe: string;
	streamId: number;
	totalCost: number;
};

interface PeriodicReportingInternalCostsProps {
	data: PeriodicReportingType;
	isCoordinator?: boolean;
	isEnabledToView?: boolean;
	refreshAmounts: () => void;
	state: 'DRAFT' | 'VALIDATED';
}

const PeriodicReportingInternalCosts: React.FC<PeriodicReportingInternalCostsProps> = ({
	data,
	isCoordinator,
	isEnabledToView,
	refreshAmounts,
	state,
}) => {
	const { pathname } = useLocation();
	const projectId = pathname.split('/')[2];
	const [filterInputValue, setFilterInputValue] = useState('');
	const [searchTerm, setSearchTerm] = useState('');

	const gridRef = useRef(null);

	const localeText = AG_GRID_LOCALE_IT;

	const [rowData, setRowData] = useState<InternalCostType[]>([]);

	const [loading, setLoading] = useState(true);
	const [count, setCount] = useState<number>();
	const [currentPage, setCurrentPage] = useState(0);
	const [perPage, setPerPage] = useState(100);
	const [defaultPerPage, setDefaultPerPage] = useState(100);

	const [pickLoading, setPickLoading] = useState(false);

	useEffect(() => {
		(async () => {
			try {
				const { data: timesheets, meta } = await getTimesheetsByProjectIdAndStatementId(projectId, data.id, {
					currentPage: currentPage + 1,
					perPage,
					searchTerm,
				});
				const formattedData = timesheets.map((item) => {
					return {
						id: item.timesheetDetailId,
						beneficiaryName: item.beneficiaryName,
						category: item.category,
						serialNumber: item.serialNumber,
						number: item.number,
						day: item.day,
						month: item.month,
						year: item.year,
						orderNumber: item.orderNumber,
						wbe: item.wbe,
						streamId: item.streamId,
						totalCost: item.totalCost,
						checked: item.checked,
						expenseItemName: item.expenseItemName,
					};
				});
				setRowData(formattedData);
				setCount(meta.total);
				if (meta.perPage !== perPage) {
					setPerPage(meta.perPage);
					setDefaultPerPage(meta.perPage);
				}
			} catch (error) {
				console.log('error', error);
			} finally {
				setLoading(false);
			}
		})();
	}, [currentPage, perPage, searchTerm, data.id, projectId]);

	const columnDefs = [
		...((isCoordinator || isEnabledToView) && state === 'DRAFT'
			? [
				{
					maxWidth: 50,
					resizable: false,
					checkboxSelection: true,
					headerCheckboxSelection: true,
					cellStyle: () => (loading || pickLoading ? { pointerEvents: 'none', opacity: '0.4' } : null),
				},
			]
			: []),
		{
			headerName: 'Beneficiario',
			field: 'beneficiaryName',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Matricola',
			field: 'serialNumber',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Categoria',
			field: 'category',
			filter: 'agTextColumnFilter',
			cellStyle: { ...customCellStyle },
			cellRendererParams: { queryParam: 'category' },
		},
		{
			headerName: 'Record',
			field: 'number',
			filter: 'agTextColumnFilter',
			tooltipField: 'number',
			tooltipComponent: CustomTooltip,
		},
		{
			headerName: 'Giorno',
			field: 'day',
			headerClass: 'text-center',
			cellRendererParams: { queryParam: 'day' },
			maxWidth: 100,
			cellStyle: { ...customCellStyle, justifyContent: 'center' },
		},
		{
			headerName: 'Mese',
			field: 'month',
			headerClass: 'text-center',
			cellStyle: { ...customCellStyle, justifyContent: 'center' },
			valueGetter: (params: { data: { month: number } }) => months()[params.data.month - 1],
		},
		{
			headerName: 'Anno',
			field: 'year',
			headerClass: 'text-center',
			maxWidth: 100,
			cellStyle: { ...customCellStyle, justifyContent: 'center' },
		},
		{
			headerName: 'Ordine',
			field: 'orderNumber',
			filter: 'agTextColumnFilter',
			cellStyle: { ...customCellStyle },
		},
		{
			headerName: 'WBE',
			field: 'wbe',
			filter: 'agTextColumnFilter',
			cellStyle: { ...customCellStyle },
		},
		{
			headerName: 'WP/Stream',
			field: 'streamId',
			filter: 'agTextColumnFilter',
			cellStyle: { ...customCellStyle, justifyContent: 'center' },
		},
		{
			headerName: "Voce di spesa",
			field: "expenseItemName",
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Costo totale €',
			field: 'totalCost',
			filter: 'agTextColumnFilter',
			type: 'rightAligned',
			cellStyle: { ...customCellStyle, justifyContent: 'right' },
			valueGetter: (params: { data: { totalCost: number } }) => (params.data.totalCost ? formatToDecimals(params.data.totalCost) : 0),
		},
	];

	const defaultColDef = {
		resizable: false,
		wrapText: true,
		autoHeight: true,
		flex: 1,
		suppressMovable: true,
		cellStyle: {
			padding: '0 1rem',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		},
	};

	const debouncedSetSearchTermRef = useRef(
		debounce((value) => {
			setCurrentPage(0);
			setPerPage(defaultPerPage);
			if (gridRef.current) {
				gridRef.current.api.paginationGoToPage(0);
				gridRef.current.api.paginationSetPageSize(defaultPerPage);
			}
			setSearchTerm(value); // Solo l'aggiornamento finale dopo debounce
		}, 500),
	);
	const onFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setFilterInputValue(value);
	};

	useEffect(() => {
		debouncedSetSearchTermRef.current(filterInputValue);
	}, [filterInputValue]);

	const handleData = (params: {
		api: {
			forEachNode: (
				arg0: (node: { setSelected: (arg0: boolean, arg1: boolean, arg2: boolean) => void; data: { checked: number } }) => void,
			) => void;
		};
	}) => {
		params.api.forEachNode((node) => {
			node.setSelected(node.data.checked === 1, false, true);
		});
	};

	const pickAll = async (timesheetIds: number[]) => {
		try {
			await pickAllTimesheetsByProjectIdAndStatementId(projectId, data.id, timesheetIds);
		} catch (error) {
			console.log('error', error);
		}
	};
	const unpickAll = async (timesheetIds: number[]) => {
		try {
			await unpickAllTimesheetsByProjectIdAndStatementId(projectId, data.id, timesheetIds);
		} catch (error) {
			console.log('error', error);
		}
	};

	return (
		<>
			<div className="d-flex justify-content-end">
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						alignItems: 'center',
						height: '96px',
					}}
				>
					<ButtonToolbar className="justify-content-end">
						<DownloadExcelButton
							apiDownload={downloadInvoicesOrTimesheetsByProjectIdAndStatementId}
							apiProps={{ projectId, statementId: data.id, docType: 'timesheets' }}
							label={translate('common.downloadreport', 'Scarica report')}
						/>
						<InputGroup className="search-table align-items-center mt-md-0 ms-1" style={{ width: '300px' }}>
							<FormControl
								type="text"
								className="filter-text-box"
								placeholder={translate('common.search', 'Cerca') + '...'}
								value={filterInputValue}
								onChange={onFilterChange}
							/>
							<div style={{ width: '40px', textAlign: 'center' }}>
								<Lens className="lens" />
							</div>
						</InputGroup>
					</ButtonToolbar>
				</div>
			</div>
			<div className="ag-theme-alpine list-table relative">
				<AgGridReact
					ref={gridRef}
					rowData={rowData}
					columnDefs={[...columnDefs]}
					defaultColDef={defaultColDef}
					domLayout="autoHeight"
					tooltipShowDelay={0}
					tooltipHideDelay={2000}
					tooltipMouseTrack={true}
					headerHeight={50}
					rowHeight={50}
					suppressCellFocus={true}
					localeText={localeText}
					rowSelection="multiple"
					rowMultiSelectWithClick={true}
					onFirstDataRendered={handleData}
					onGridReady={handleData}
					onSelectionChanged={(event) => {
						(async () => {
							setPickLoading(true);
							const selectedRows = event.api.getSelectedRows();
							const idsToPick = selectedRows.map((row: InternalCostType) => row.id);

							await pickAll(idsToPick);

							const allRows = event.api.getRenderedNodes().map((node) => node.data);
							const idsToUnpick = allRows.filter((row) => !idsToPick.includes(row.id)).map((row) => row.id);

							await unpickAll(idsToUnpick);
							refreshAmounts();
							setPickLoading(false);
						})();
					}}
					onPaginationChanged={(event) => {
						if (event.newData === undefined) return;
						handleData(event);
					}}
				/>
			</div>
			<CustomPagination
				count={count || 100}
				loading={loading}
				currentPage={currentPage || 0}
				setCurrentPage={setCurrentPage}
				perPage={perPage || 100}
				setPerPage={setPerPage}
				gridRef={gridRef}
			/>
		</>
	);
};

export default PeriodicReportingInternalCosts;
